import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  Webhook, 
  Shield, 
  AlertTriangle, 
  CheckCircle, 
  Loader2, 
  Trash2,
  ExternalLink,
  Lock,
  Globe,
  FileText,
  Clock
} from 'lucide-react';

const ATSIntegrationSettings = () => {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [sccDpfAccepted, setSccDpfAccepted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);

  useEffect(() => {
    fetchStatus();
    fetchLogs();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await employerAPI.getATSWebhookStatus();
      setStatus(response.data);
      if (response.data.webhook_url) {
        setWebhookUrl(response.data.webhook_url);
      }
    } catch (error) {
      console.error('Error fetching webhook status:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await employerAPI.getATSLogs();
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
    }
  };

  const handleSave = async () => {
    if (!webhookUrl) {
      toast.error('Please enter a webhook URL');
      return;
    }

    if (!sccDpfAccepted) {
      toast.error('You must accept the EU-US Data Privacy Framework / SCC terms');
      return;
    }

    // Validate URL
    try {
      new URL(webhookUrl);
    } catch {
      toast.error('Please enter a valid URL');
      return;
    }

    setSaving(true);
    try {
      await employerAPI.configureATSWebhook({
        webhook_url: webhookUrl,
        scc_dpf_accepted: sccDpfAccepted,
        webhook_secret: webhookSecret || null
      });
      toast.success('ATS webhook configured successfully');
      fetchStatus();
    } catch (error) {
      console.error('Error saving webhook:', error);
      toast.error(error.response?.data?.detail || 'Failed to configure webhook');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      await employerAPI.removeATSWebhook();
      toast.success('Webhook removed');
      setStatus(null);
      setWebhookUrl('');
      setWebhookSecret('');
      setSccDpfAccepted(false);
      setShowRemoveConfirm(false);
    } catch (error) {
      toast.error('Failed to remove webhook');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString();
  };

  if (loading) {
    return (
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-[#1B5AFF]" />
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
            <Webhook className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#0A0F1E]">ATS Integration (Push API)</h2>
            <p className="text-[#5B6472] text-sm">
              Configure webhook to receive candidate data when they accept your requests
            </p>
          </div>
          {status?.webhook_configured && (
            <Badge className="ml-auto bg-green-500/20 text-green-400">
              <CheckCircle className="w-3 h-3 mr-1" />
              Active
            </Badge>
          )}
        </div>

        {/* GDPR/FCRA Notice */}
        <div className="bg-[#FFFFFF] border border-amber-500/30 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-400 mt-0.5" />
            <div>
              <p className="text-amber-400 font-medium text-sm mb-1">GDPR & FCRA Compliant Push Architecture</p>
              <p className="text-[#5B6472] text-xs">
                VETT uses a <strong>push-only</strong> webhook model. Candidate data is only sent to your ATS 
                when a candidate explicitly accepts your connection request. You cannot pull or query 
                candidate data. Each payload includes legal disclaimers transferring FCRA compliance 
                obligations to your organization.
              </p>
            </div>
          </div>
        </div>

        {/* Configuration Form */}
        <div className="space-y-4">
          <div>
            <Label className="text-[#0A0F1E] mb-2 block">Webhook URL</Label>
            <Input
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://your-ats.com/api/webhooks/vett"
              className="bg-[#F5F8FF] border-[#E8EDF5] text-[#0A0F1E]"
              data-testid="webhook-url-input"
            />
            <p className="text-[#5B6472] text-xs mt-1">
              Your ATS endpoint that will receive candidate data via POST requests
            </p>
          </div>

          <div>
            <Label className="text-[#0A0F1E] mb-2 block">Webhook Secret (Optional)</Label>
            <Input
              type="password"
              value={webhookSecret}
              onChange={(e) => setWebhookSecret(e.target.value)}
              placeholder="Enter a secret for HMAC signature verification"
              className="bg-[#F5F8FF] border-[#E8EDF5] text-[#0A0F1E]"
              data-testid="webhook-secret-input"
            />
            <p className="text-[#5B6472] text-xs mt-1">
              If provided, we will sign each payload with HMAC-SHA256 in the X-VETT-Signature header
            </p>
          </div>

          {/* SCC/DPF Consent - REQUIRED */}
          <div className="bg-[#F5F8FF] border border-[#E8EDF5] rounded-lg p-4">
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-[#1B5AFF] mt-0.5" />
              <div className="flex-1">
                <p className="text-[#0A0F1E] font-medium text-sm mb-2">
                  Cross-Border Data Transfer Agreement
                </p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={sccDpfAccepted}
                    onCheckedChange={setSccDpfAccepted}
                    className="mt-1 border-[#E8EDF5]"
                    data-testid="scc-dpf-checkbox"
                  />
                  <span className="text-[#0A0F1E] text-sm leading-relaxed">
                    I certify that my organization complies with the <strong>EU-US Data Privacy Framework</strong> or 
                    agrees to the <strong>Standard Contractual Clauses (SCCs)</strong> for cross-border data transfer. 
                    I understand that candidate data transferred via this webhook is subject to GDPR and 
                    my organization assumes all FCRA compliance obligations including Adverse Action notice 
                    requirements.
                  </span>
                </label>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              disabled={saving || !sccDpfAccepted}
              className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] disabled:opacity-50"
              data-testid="save-webhook-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Lock className="w-4 h-4 mr-2" />
              )}
              {status?.webhook_configured ? 'Update Webhook' : 'Configure Webhook'}
            </Button>

            {status?.webhook_configured && (
              <Button
                onClick={() => setShowRemoveConfirm(true)}
                variant="outline"
                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Remove
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Webhook Logs */}
      {logs.length > 0 && (
        <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-5 h-5 text-[#1B5AFF]" />
            <h3 className="text-lg font-semibold text-[#0A0F1E]">Recent Webhook Activity</h3>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {logs.map((log) => (
              <div 
                key={log.id} 
                className="bg-[#F5F8FF] rounded-lg p-3 border border-[#E8EDF5]"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {log.event_type.includes('completed') ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : log.event_type.includes('failed') ? (
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                    ) : (
                      <Clock className="w-4 h-4 text-[#5B6472]" />
                    )}
                    <span className="text-[#0A0F1E] text-sm">
                      {log.event_type.replace(/_/g, ' ')}
                    </span>
                  </div>
                  <span className="text-[#5B6472] text-xs">{formatDate(log.timestamp)}</span>
                </div>
                {log.details?.status_code && (
                  <p className="text-[#5B6472] text-xs mt-1">
                    Status: {log.details.status_code}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Payload Example */}
      <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-6">
        <div className="flex items-center gap-3 mb-4">
          <ExternalLink className="w-5 h-5 text-[#1B5AFF]" />
          <h3 className="text-lg font-semibold text-[#0A0F1E]">Webhook Payload Example</h3>
        </div>

        <pre className="bg-[#FFFFFF] rounded-lg p-4 text-xs text-[#0A0F1E] overflow-x-auto">
{`{
  "legal_disclaimer": "This data is provided for employment purposes only. The end-user assumes all FCRA compliance obligations, including Adverse Action notice requirements, regarding the use of this data. Under GDPR, this data is transferred pursuant to candidate consent and must be deleted upon candidate request.",
  "candidate_data": {
    "vett_id": "VETT-ABC123",
    "email": "candidate@example.com",
    "profile": {
      "first_name": "Jane",
      "last_name": "Doe",
      "job_title": "Senior Engineer",
      "location": "New York, NY",
      "skills": ["React", "Python", "AWS"]
    },
    "consent": {
      "granted_at": "2026-06-19T12:00:00Z",
      "type": "profile_share"
    }
  },
  "pushed_at": "2026-06-19T12:00:05Z",
  "source": "VETT Platform",
  "version": "1.0"
}`}
        </pre>
      </Card>

      {/* Remove Confirmation Dialog */}
      <Dialog open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5]">
          <DialogHeader>
            <DialogTitle className="text-[#0A0F1E]">Remove Webhook</DialogTitle>
            <DialogDescription className="text-[#5B6472]">
              Are you sure you want to remove the ATS webhook? You will no longer receive 
              candidate data pushes when they accept your requests.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRemoveConfirm(false)}
              className="border-[#E8EDF5] text-[#0A0F1E]"
            >
              Cancel
            </Button>
            <Button
              onClick={handleRemove}
              className="bg-red-600 hover:bg-red-700 text-[#FFFFFF]"
            >
              Remove Webhook
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ATSIntegrationSettings;
